import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import Home from '@/views/Home.vue';
import Categories from '@/views/Categories.vue';
import Search from '@/views/Search.vue';
import Favorites from '@/views/Favorites.vue';
import WallpaperDetail from '@/views/WallpaperDetail.vue';
import AdminDashboard from '@/views/AdminDashboard.vue';
import PrivacyPolicy from '@/views/PrivacyPolicy.vue';
import TermsOfService from '@/views/TermsOfService.vue';
import Contact from '@/views/Contact.vue';
import { useAdmin } from '@/composables/useAdmin';

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: {
      title: 'Free HD Wallpapers & Backgrounds - WallpaperHub',
      description: 'Download thousands of free HD wallpapers and backgrounds for desktop, mobile, and tablet. Browse by categories: Nature, Abstract, Gaming, Minimalist, and more. All wallpapers are high-quality and free to use.',
      keywords: 'free wallpapers, HD wallpapers, desktop backgrounds, mobile wallpapers, 4K wallpapers, nature wallpapers, abstract wallpapers, gaming wallpapers, minimalist wallpapers, free download',
      robots: 'index, follow',
      ogType: 'website',
      ogImage: '/images/og-home.jpg'
    }
  },
  {
    path: '/categories',
    name: 'Categories',
    component: Categories,
    meta: {
      title: 'Wallpaper Categories - Browse by Theme | WallpaperHub',
      description: 'Explore wallpapers organized by categories. Find the perfect wallpaper from Nature, Abstract, Technology, Animals, Gaming, Minimalist, and many more themed collections.',
      keywords: 'wallpaper categories, nature wallpapers, abstract wallpapers, technology wallpapers, animal wallpapers, gaming wallpapers, minimalist wallpapers, themed wallpapers',
      robots: 'index, follow',
      ogType: 'website'
    }
  },
  {
    path: '/categories/:category',
    name: 'CategoryDetail',
    component: () => import('@/views/CategoryDetail.vue'),
    meta: {
      title: ':category Wallpapers - Free HD Downloads | WallpaperHub',
      description: 'Browse and download free :category wallpapers in HD and 4K quality. Perfect for desktop, mobile, and tablet backgrounds.',
      keywords: ':category wallpapers, :category backgrounds, HD :category wallpapers, 4K :category wallpapers, free :category wallpapers download',
      robots: 'index, follow',
      ogType: 'website'
    }
  },
  {
    path: '/search',
    name: 'Search',
    component: Search,
    meta: {
      title: 'Search Wallpapers - Find Your Perfect Background | WallpaperHub',
      description: 'Search through thousands of free HD wallpapers. Find wallpapers by keyword, color, resolution, or theme. All wallpapers available for free download.',
      keywords: 'search wallpapers, find wallpapers, wallpaper search engine, HD wallpaper search, free wallpaper finder',
      robots: 'index, follow',
      ogType: 'website'
    }
  },
  {
    path: '/favorites',
    name: 'Favorites',
    component: Favorites,
    meta: {
      title: 'My Favorite Wallpapers - WallpaperHub',
      description: 'Access your saved favorite wallpapers. Keep track of wallpapers you love and download them anytime.',
      keywords: 'favorite wallpapers, saved wallpapers, wallpaper collection, my wallpapers',
      robots: 'noindex, follow',
      ogType: 'website'
    }
  },
  {
    path: '/wallpaper/:id',
    name: 'WallpaperDetail',
    component: WallpaperDetail,
    meta: {
      title: ':title - Free HD Wallpaper Download | WallpaperHub',
      description: 'Download this beautiful :title wallpaper in multiple resolutions. Available in HD, 4K, and mobile-optimized sizes. Free for personal and commercial use.',
      keywords: ':title wallpaper, :category wallpaper, HD wallpaper download, 4K wallpaper, free wallpaper, :tags',
      robots: 'index, follow',
      ogType: 'article',
      ogImage: ':imageUrl'
    }
  },
  {
    path: '/admin',
    name: 'AdminLogin',
    component: () => import('@/components/AdminLogin.vue'),
    beforeEnter: (to, from, next) => {
      const { isAdmin } = useAdmin();

      // If user is already authenticated as admin, redirect to dashboard
      if (isAdmin.value) {
        next('/admin/dashboard');
      } else {
        next();
      }
    }
  },
  {
    path: '/admin/dashboard',
    name: 'AdminDashboard',
    component: AdminDashboard
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: PrivacyPolicy,
    meta: {
      title: 'Privacy Policy - WallpaperHub',
      description: 'Learn how WallpaperHub collects, uses, and protects your personal information. Our comprehensive privacy policy covers data collection, cookies, third-party services, and your rights under GDPR.',
      keywords: 'privacy policy, data protection, GDPR, cookies, personal information, wallpaper app, data collection, user rights, third-party services, AdSense privacy',
      robots: 'index, follow',
      ogType: 'website'
    }
  },
  {
    path: '/terms-of-service',
    name: 'TermsOfService',
    component: TermsOfService,
    meta: {
      title: 'Terms of Service - WallpaperHub',
      description: 'Read our Terms of Service to understand the rules and conditions for using WallpaperHub, including acceptable use, user responsibilities, intellectual property rights, and service limitations.',
      keywords: 'terms of service, user agreement, wallpaper app terms, acceptable use policy, user responsibilities, intellectual property, service conditions, user conduct, legal terms, service agreement',
      robots: 'index, follow',
      ogType: 'website'
    }
  },
  {
    path: '/contact',
    name: 'Contact',
    component: Contact,
    meta: {
      title: 'Contact Us - WallpaperHub',
      description: 'Get in touch with WallpaperHub for support, technical issues, business inquiries, or privacy concerns. Multiple contact methods available with fast response times and dedicated support teams.',
      keywords: 'contact us, customer support, technical support, business inquiries, wallpaper app support, help center, contact information, support team, get in touch, customer service',
      robots: 'index, follow',
      ogType: 'website'
    }
  },
  {
    path: '/blog',
    name: 'Blog',
    component: () => import('@/views/Blog.vue'),
    meta: {
      title: 'Wallpaper Blog - Tips, Trends & Design Inspiration | WallpaperHub',
      description: 'Discover the latest wallpaper trends, design tips, and inspiration. Learn how to choose the perfect backgrounds for your devices with our expert guides and articles.',
      keywords: 'wallpaper blog, wallpaper trends, design tips, wallpaper guide, background design, desktop customization, mobile wallpapers, wallpaper inspiration',
      robots: 'index, follow',
      ogType: 'website'
    }
  },
  {
    path: '/blog/:slug',
    name: 'BlogPost',
    component: () => import('@/views/BlogPost.vue'),
    meta: {
      title: ':title | WallpaperHub Blog',
      description: ':excerpt',
      keywords: ':tags',
      robots: 'index, follow',
      ogType: 'article',
      ogImage: ':featuredImage'
    }
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL || '/'),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // If there's a saved position (browser back/forward), use it
    if (savedPosition) {
      return savedPosition;
    }

    // If navigating to a hash anchor, scroll to that element
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth'
      };
    }

    // For all other navigation, scroll to top
    return {
      top: 0,
      left: 0,
      behavior: 'smooth'
    };
  }
});

// Global navigation guard to handle SEO meta tags
router.beforeEach((to, from, next) => {
  // Set page title from route meta or use default
  if (to.meta?.title) {
    document.title = to.meta.title as string;
  }

  // Set meta description
  if (to.meta?.description) {
    let metaDescription = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', to.meta.description as string);
  }

  // Set meta keywords
  if (to.meta?.keywords) {
    let metaKeywords = document.querySelector('meta[name="keywords"]') as HTMLMetaElement;
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', to.meta.keywords as string);
  }

  // Set robots meta
  if (to.meta?.robots) {
    let metaRobots = document.querySelector('meta[name="robots"]') as HTMLMetaElement;
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute('content', to.meta.robots as string);
  }

  // Set canonical URL
  const canonicalUrl = `${window.location.origin}${to.path}`;
  let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalLink);
  }
  canonicalLink.setAttribute('href', canonicalUrl);

  // Set Open Graph tags
  if (to.meta?.title) {
    let ogTitle = document.querySelector('meta[property="og:title"]') as HTMLMetaElement;
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', to.meta.title as string);
  }

  if (to.meta?.description) {
    let ogDescription = document.querySelector('meta[property="og:description"]') as HTMLMetaElement;
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.setAttribute('content', to.meta.description as string);
  }

  if (to.meta?.ogType) {
    let ogType = document.querySelector('meta[property="og:type"]') as HTMLMetaElement;
    if (!ogType) {
      ogType = document.createElement('meta');
      ogType.setAttribute('property', 'og:type');
      document.head.appendChild(ogType);
    }
    ogType.setAttribute('content', to.meta.ogType as string);
  }

  let ogUrl = document.querySelector('meta[property="og:url"]') as HTMLMetaElement;
  if (!ogUrl) {
    ogUrl = document.createElement('meta');
    ogUrl.setAttribute('property', 'og:url');
    document.head.appendChild(ogUrl);
  }
  ogUrl.setAttribute('content', canonicalUrl);

  // Set site name
  let ogSiteName = document.querySelector('meta[property="og:site_name"]') as HTMLMetaElement;
  if (!ogSiteName) {
    ogSiteName = document.createElement('meta');
    ogSiteName.setAttribute('property', 'og:site_name');
    document.head.appendChild(ogSiteName);
  }
  ogSiteName.setAttribute('content', 'WallpaperHub');

  next();
});

export default router;